import React, { useState } from 'react'
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import moment from "moment-timezone";
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import { pwaSettings } from 'configs/pwaConstants';
import LAOChartCard from './LAO_Common/LAOChartCard';
import { chartsHeight as height } from 'helpers/styleHelpers';
import { CardActions, Collapse } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import useSetQuery, { useMultiSetQuery } from 'customHooks/useQuery';

export const useStylesExport = makeStyles((theme) => ({
  rootClassExport: {
    height: "auto",
    minHeight: theme.spacing(25),
    borderRadius: theme.spacing(1),
    background: theme.palette.card.default,
  },
  titleClassExport: {
    marginRight: "auto",
    paddingBottom: theme.spacing(2),
    fontSize: theme.spacing(2),
    textTransform: "none",
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 0,
    }
  },
  sliderClassExport: {
    margin: `0px ${theme.spacing(2.25)}px ${theme.spacing(1)}px`,
    "& .slick-current": {
      display: "flex",
      justifyContent: "center"
    }
  },
  expandExport: {
    textTransform: "capitalize",
    textDecoration: "none",
    border: "none"
  },
  cardactionsExport: {
    justifyContent: "center"
  }
}));

const LaoExportKpiWrapper = ({ defaultFilters }) => {

  const classesExport = useStylesExport({ height });
  const { t } = useTranslation();
  const [params] = useSetQuery();
  const setMultiParams = useMultiSetQuery();
  const isMobileExport = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [innerWidthExport] = useState(() => window.innerWidth - window.innerWidth / 10 * 1.5)
  const [expanded, setExpanded] = useState(false);

  const subTypeValues = {
    // Export Line Items Planned
    "PO_ITEMS_WITH_LD_NBR": "EXPORT_KPI_LINE_ITEMS_WITH_LD",
    "PO_ITEMS_WITHOUT_LD_NBR": "EXPORT_KPI_LINE_ITEMS_WITHOUT_LD",

    // Export Units (Containers/Trucks)
    "EXPORT_KPI_EXPORT_UNITS_EXPORTS_UNIT": "EXPORT_KPI_EXPORT_UNITS_TOTAL",
    "EXPORT_KPI_EXPORT_UNITS_INVOICED_UNITS": "EXPORT_KPI_EXPORT_UNITS_INVOICED",
    "EXPORT_KPI_EXPORT_UNITS_BILLING_PENDING": "EXPORT_KPI_EXPORT_UNITS_BILLING_PENDING",
    "EXPORT_KPI_EXPORT_UNITS_LDS_INVOICED": "EXPORT_KPI_EXPORT_UNITS_INVOICED",
    "EXPORT_KPI_EXPORT_UNITS_LDS_BILLING_PENDING": "EXPORT_KPI_EXPORT_UNITS_BILLING_PENDING",

    // Exports Unit with Shipment
    "EXPORT_KPI_EXPORT_UNITS_WITH_SHIPMENT_EXPORTS_UNIT_INVOICED": "EXPORT_KPI_EXPORT_UNITS_INVOICED",
    "EXPORT_KPI_EXPORT_UNITS_WITH_SHIPMENT_PO_ITEMS_CT_WITH_SHIPMENT": "EXPORT_KPI_UNITS_WITH_SHIPMENT",
    "EXPORT_KPI_EXPORT_UNITS_WITH_SHIPMENT_PO_ITEMS_CT_WITHOUT_SHIPMENT": "EXPORT_KPI_UNITS_WITHOUT_SHIPMENT",
    "EXPORT_KPI_EXPORT_UNITS_WITH_SHIPMENT_LDS_INVOICED_WITH_SHIPMENT": "EXPORT_KPI_UNITS_WITH_SHIPMENT",
    "EXPORT_KPI_EXPORT_UNITS_WITH_SHIPMENT_LDS_INVOICED_WITHOUT_SHIPMENT": "EXPORT_KPI_UNITS_WITHOUT_SHIPMENT",

    // Export Units Invoiced by Last 6 months
    "EXPORT_KPI_EXPORT_UNITS_INVOICED": "EXPORT_KPI_UNITS_BY_MONTH",

    // Export Units by Shipment Mode
    "EXPORT_KPI_EXPORT_UNITS_BY_SHIPMENT_MODE_OCEAN": "EXPORT_KPI_UNITS_MODE_OCEAN",
    "EXPORT_KPI_EXPORT_UNITS_BY_SHIPMENT_MODE_LAND": "EXPORT_KPI_UNITS_MODE_LAND",
    "EXPORT_KPI_EXPORT_UNITS_BY_SHIPMENT_MODE_AIR": "EXPORT_KPI_UNITS_MODE_AIR",

    // Export Units by Carrier
    "EXPORT_KPI_EXPORT_UNITS_BY_CARRIER": "EXPORT_KPI_CARRIER",

    // % Export Units by Status	
    "EXPORT_KPI_EXPORT_UNITS_BY_CARRIER_STATUS_BID": "EXPORT_KPI_CARRIER_BID",
    "EXPORT_KPI_EXPORT_UNITS_BY_CARRIER_STATUS_SPOT": "EXPORT_KPI_CARRIER_SPOT"
  }

  const setDetails = (filters = {}) => {
    const {
      // uncomment below lines to Remove invoiceDateRange, exportInternationalShipperWithSeparator filters 
      // invoiceDateRange,
      // exportInternationalShipperWithSeparator,
      ...prevFilters
    } = params.laoFilters

    setMultiParams({
      laoFilters: JSON.stringify({
        ...prevFilters,
        ...filters
      }),
      isHyperlink: true
    });
  }

  const handleExpandClick = () => {
    setExpanded(!expanded);
  }

  const chartsExportTop = [
    {
      title: t("export_line_items_planned"),
      subtype: "EXPORT_KPI_LINE_ITEMS_PLANNED",
      chart: "trendline",
      colorPalette: "set13",
      displayKey: "stateDesc",
      tooltipKey: "stateDesc",
      colorBy: "xKey",
      showXAxisLabel: true,
      testId: "export-line-items-planned-chart",
      page: "exportKPI",
      height: 400,
      minWidth: 500,
      maxWidth: 500,
      showTotal: true,
      setDetailsData: true,
      setDetails: (data) => {
        const filters = { searchSubType: subTypeValues[data?.stateKey] }
        setDetails(filters) // table filter
      }
    },
    {
      title: t("export_units_containers_trucks"),
      subtype: "EXPORT_KPI_EXPORT_UNITS",
      chart: "pie",
      height: 400,
      minWidth: 500,
      maxWidth: 500,
      colorPalette: "set15",
      testId: "export_units-chart",
      page: "exportKPI",
      text1: "exports_unit",
      text2: "invoiced",
      text3: "billing_pending",
      showTotal: true,
      customNoTextHeight: 3,
      showSliceLabel: true,
      keys: ["totalRatio"],
      yKey: "totalRatio",
      tooltipKey: "stateDesc",
      displayKey: "stateDesc",
      xKey: "stateDesc",
      setDetails: (data) => {
        const key = `${data?.valueType}_${data?.stateKey.toUpperCase()}`
        const filters = { searchSubType: subTypeValues[key] }
        setDetails(filters) // table filter
      },
      setDetailsData: true,
      stateKey1: "EXPORTS_UNIT",
      stateKey2: "INVOICED_UNITS",
      stateKey3: "BILLING_PENDING",
      isLegendClickable: false
    },
    {
      title: t("exports_unit_with_shipment"),
      subtype: "EXPORT_KPI_EXPORT_UNITS_WITH_SHIPMENT",
      chart: "pie",
      height: 400,
      minWidth: 500,
      maxWidth: 500,
      colorPalette: "set21",
      testId: "exports_unit_with_shipment-chart",
      page: "exportKPI",
      text1: "exports_unit_invoiced",
      text2: "po_items_ct_with_shipment",
      text3: "po_items_ct_without_shipment",
      showTotal: true,
      customNoTextHeight: 3,
      showSliceLabel: true,
      keys: ["totalRatio"],
      yKey: "totalRatio",
      tooltipKey: "state",
      displayKey: "state",
      xKey: "state",
      setDetails: (data) => {
        const key = `${data?.valueType}_${data?.stateKey.toUpperCase()}`
        const filters = { searchSubType: subTypeValues[key] }
        setDetails(filters) // table filter
      },
      setDetailsData: true,
      stateKey1: "EXPORTS_UNIT_INVOICED",
      stateKey2: "PO_ITEMS_CT_WITH_SHIPMENT",
      stateKey3: "PO_ITEMS_CT_WITHOUT_SHIPMENT",
      isLegendClickable: false
    }];

  const chartsExportBottom = [
    {
      title: t("export_units_invoiced_by_last_6_months"),
      subtype: "EXPORT_KPI_EXPORT_UNITS_INVOICED",
      chart: "trendline",
      height: 400,
      maxWidth: 500,
      minWidth: 500,
      colorPalette: "set16",
      colorBy: "xKey",
      showTotal: true,
      yLabel: '',
      showXAxisLabel: !isMobileExport,
      startDate: 'state',
      xKey: 'formattedStartDate',
      keys: ['totalCount'],
      yKey: 'totalCount',
      themeType: 'dark',
      page: "exportKPI",
      setDetails: (data) => {
        const filters = {
          searchSubType: subTypeValues[data?.valueType],
          invoiceDateRange: { startTime: moment(data.state, "MM-YY").format("YYYY-MM-DD"), endTime: moment(data.state, "MM-YY").format("YYYY-MM-DD")}
        }
        setDetails(filters) // table filter
      },
      setDetailsData: true,
      testId: "export_units_invoiced_by_last_6_months-chart",
    },
    {
      title: t("export_units_by_shipment_mode"),
      subtype: "EXPORT_KPI_EXPORT_UNITS_BY_SHIPMENT_MODE",
      chart: "pie",
      text1: "ocean",
      text2: "land",
      text3: "air",
      height: 400,
      minWidth: 500,
      maxWidth: 500,
      colorPalette: "set20",
      testId: "export_units_by_shipment_mode-chart",
      page: "exportKPI",
      showTotal: true,
      showSliceLabel: true,
      keys: ["totalRatio"],
      yKey: "totalRatio",
      setDetails: (data) => {
        const key = `${data?.valueType}_${data?.stateKey.toUpperCase()}`
        const filters = { searchSubType: subTypeValues[key] }
        setDetails(filters) // table filter
      },
      setDetailsData: true,
      stateKey1: "OCEAN",
      stateKey2: "LAND",
      stateKey3: "AIR",
      isLegendClickable: false,
    },
    {
      title: t("export_units_by_carrier"),
      subtype: "EXPORT_KPI_EXPORT_UNITS_BY_CARRIER",
      chart: "trendline",
      height: 400,
      maxWidth: 500,
      minWidth: 500,
      colorPalette: "set19",
      colorBy: "yKey",
      showTotal: false,
      showXAxisLabel: !isMobileExport,
      xKey: 'stateDesc',
      yKey: 'totalCount',
      keys: ['totalCount'],
      setDetails: (data) => {
        const filters = {
          searchSubType: subTypeValues[data?.valueType],
          exportInternationalShipperWithSeparator: data?.stateKey
        }
        setDetails(filters) // table filter
      },
      setDetailsData: true,
      themeType: 'dark',
      page: "exportKPI",
      horizontal: true,
      testId: "export_units_by_carrier-chart",
    },
    {
      title: t("export_units_by_status"),
      subtype: "EXPORT_KPI_EXPORT_UNITS_BY_CARRIER_STATUS",
      chart: "pie",
      text1: "bid",
      text2: "spot",
      height: 400,
      minWidth: 500,
      maxWidth: 500,
      colorPalette: "set18",
      testId: "export_units_by_status-chart",
      page: "exportKPI",
      showTotal: true,
      showSliceLabel: true,
      keys: ["totalRatio"],
      yKey: "totalRatio",
      setDetails: (data) => {
        const key = `${data?.valueType}_${data?.stateKey.toUpperCase()}`
        const filters = { searchSubType: subTypeValues[key] }
        setDetails(filters) // table filter     
      },
      setDetailsData: true,
      stateKey1: "BID",
      stateKey2: "SPOT",
      isLegendClickable: false
    }
  ];

  return (
    <Card className={classesExport.rootClassExport} data-testid="lao-export-kpis-wrapper">
      <CardHeader
        title={<Typography className={classesExport.titleClassExport} variant="h3" component={"h3"}>{t("live_status_text").toUpperCase()}</Typography>}
      />
      {
        isMobileExport && <>
          <Slider {...pwaSettings} className={classesExport.sliderClassExport}>
            {
              [...chartsExportTop, ...chartsExportBottom].map(d => {
                return (
                  <div>
                    <LAOChartCard {...d} maxWidth={innerWidthExport} minWidth={innerWidthExport} boxShadow="none" defaultFilters={defaultFilters} />
                  </div>
                )
              })
            }
          </Slider>
        </>
      }
      {
        !isMobileExport && <>
          <CardContent p={0}>
            <Grid
              container
              spacing={3}
              alignItems="center"
              justifyContent="center"
            >
              {
                chartsExportTop.map(d => {
                  return (
                    <Grid item p={1}>
                      <LAOChartCard {...d} defaultFilters={defaultFilters} />
                    </Grid>
                  )
                })
              }
            </Grid>
          </CardContent>
          <Collapse in={expanded} timeout="auto">
            <CardContent p={0} data-testid="expandableExport-content">
              <Grid
                container
                spacing={3}
                alignItems="center"
                justifyContent="center"
              >
                {
                  chartsExportBottom.map(d => {
                    return (
                      <Grid item p={1}>
                        <LAOChartCard {...d} defaultFilters={defaultFilters} />
                      </Grid>
                    )
                  })
                }

              </Grid>
            </CardContent>
          </Collapse>

          <CardActions className={classesExport.cardactionsExport}>
            <Button
              variant="outlined"
              color="textSecondary"
              className={classesExport.expandExport}
              onClick={handleExpandClick}
              disableRipple
              data-testid="expandExport"
              disableFocusRipple
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {expanded ? t('view_less') : t('view_more')}
            </Button>
          </CardActions>
        </>
      }
    </Card>
  );
}

export default LaoExportKpiWrapper